import core = egr.wcf.core;
import './index.css';
/**
 * This components creates the views and the controls of the viewer.
 */

export interface ViewerOptions {
    multiView: boolean;
    disableCameraPanning: boolean;
    limitCameraDistanceByElementRadius: boolean;
}
class ViewerUI {
    private mCoreApp: core.Application;
    private mOptions: ViewerOptions;

    /**
     * @param pMultiView If true, four views will be created instead of one.
     */
    constructor(pCoreApp: core.Application, pOptions: ViewerOptions) {
        this.mCoreApp = pCoreApp;
        this.mOptions = pOptions;
        // create views
        if (!this.mOptions.multiView) {
            const tView: core.view.View = new core.view.View(this.mCoreApp.viewer, new BABYLON.Viewport(0, 0, 1, 1));
            if (this.mOptions.disableCameraPanning) {
                tView.cameraControl.panningEnabled = false;
                tView.cameraControl.setFixedTarget(BABYLON.Vector3.Zero());
                tView.cameraControl.dblClickZoomToFitOptions.adjustFixedTarget = false;
            }
            // tView.cameraControl.orbitInertia = 0.95; // uncomment if you want some movement after the user releases mouse button
            this.mCoreApp.viewer.addView(tView);
        } else {
            const tPerspectiveView: core.view.View = new core.view.View(this.mCoreApp.viewer, new BABYLON.Viewport(0, 0, 0.67, 1));
            if (this.mOptions.disableCameraPanning) {
                tPerspectiveView.cameraControl.panningEnabled = false;
                tPerspectiveView.cameraControl.setFixedTarget(BABYLON.Vector3.Zero());
                tPerspectiveView.cameraControl.dblClickZoomToFitOptions.adjustFixedTarget = false;
            }
            this.mCoreApp.viewer.addView(tPerspectiveView);

            const tTopView: core.view.View = new core.view.View(this.mCoreApp.viewer, new BABYLON.Viewport(0.67, 0, 0.33, 0.33));
            tTopView.cameraControl.cameraMode = core.view.CameraMode.Top;
            tTopView.cameraControl.orbitEnabled = false;
            this.mCoreApp.viewer.addView(tTopView);

            const tFrontView: core.view.View = new core.view.View(this.mCoreApp.viewer, new BABYLON.Viewport(0.67, 0.33, 0.33, 0.34));
            tFrontView.cameraControl.cameraMode = core.view.CameraMode.Front;
            tFrontView.cameraControl.orbitEnabled = false;
            this.mCoreApp.viewer.addView(tFrontView);

            const tRightView: core.view.View = new core.view.View(this.mCoreApp.viewer, new BABYLON.Viewport(0.67, 0.66, 0.34, 0.34));
            tRightView.cameraControl.cameraMode = core.view.CameraMode.Right;
            tRightView.cameraControl.orbitEnabled = false;
            this.mCoreApp.viewer.addView(tRightView);
        }

        // add user interaction with the articles in the 3d view
        const tDefaultTool: core.tool.SelectionDefault = new core.tool.SelectionDefault(this.mCoreApp);
        tDefaultTool.deselectionEnabled = false;
        tDefaultTool.showMainElementSelection = false;
        tDefaultTool.showElementInteractors = true;
        this.mCoreApp.tools.defaultTool = tDefaultTool;
        this.mCoreApp.tools.startDefaultTool();

        this.mCoreApp.rendering.addShadowGenerator(new core.rendering.ShadowPlane(this.mCoreApp));
    }

    /**
     * If we have multiple articles in the scene (i.e. by loading a .pec), then we need to show the user which one is currently selected by a bounding box.
     */
    public allowMainArticleSelection(pValue: boolean): void {
        if (this.mCoreApp.tools.defaultTool instanceof core.tool.SelectionDefault) {
            this.mCoreApp.tools.defaultTool.showMainElementSelection = pValue;
            this.mCoreApp.tools.defaultTool.deselectionEnabled = pValue;
        }
    }

    /**
     * Should by called after inserting new elements.
     */
    public resetCamera(): void {
        this.mCoreApp.viewer.views.forEach((pView) => {
            if (this.mOptions.disableCameraPanning) {
                pView.cameraControl.setFixedTarget(this.getCenterOfSceneElement());
            }
            if (this.mOptions.limitCameraDistanceByElementRadius) {
                const tRadiusMultiplier: number = 3; // should be changed to value you want
                const tMaxZoomRadius: number = this.getRadiusOfSceneElement() * tRadiusMultiplier; // tMaxZoomRadius can also be a fixed value, but we take size of element into count
                pView.cameraControl.setNavigationArea(tMaxZoomRadius, this.getCenterOfSceneElement());
            }
            pView.cameraControl.setPosition(core.view.CameraControl.DEFAULT_CAMERA_POSITION);
            pView.cameraControl.zoomToFitElements(null);
        });
        this.mCoreApp.viewer.requestRenderFrame();
    }
    private getCenterOfSceneElement(): BABYLON.Vector3 {
        if (this.mCoreApp.model.elements.length > 0 && this.mCoreApp.model.elements[0].boundingBox.isValid()) {
            return this.mCoreApp.model.elements[0].boundingBox.getCenter();
        }
        return BABYLON.Vector3.Zero();
    }
    private getRadiusOfSceneElement(): number {
        if (this.mCoreApp.model.elements.length > 0 && this.mCoreApp.model.elements[0].boundingBox.isValid()) {
            return this.mCoreApp.model.elements[0].boundingBox.getRadius();
        }
        return 20; // 20 meter, if nothing is in the scene
    }
}
export default ViewerUI;